import React, { useEffect } from 'react';
import { Text } from 'react-native';
import Input from '@/components/General/inputs/Input';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import InputSection from '@/components/General/inputs/InputSection';
import Toast from 'react-native-toast-message';
import { saveRoleFolder } from '@/data/roles';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import { useEditScreenStore } from '@/data/store/useEditScreenStore';
import { useLocalSearchParams, useRouter } from 'expo-router';
import ModalWrapper from '@/components/General/ModalWrapper';
import { StyleSheet } from 'react-native-unistyles';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import useOrganizationEntitlements from '@/utils/hooks/useOrganizationEntitlements';
import { isFreeOrg } from '@/utils/organizationEntitlements';
import PlanRestrictedScreen from '@/components/General/PlanRestrictedScreen';

const EMPTY_DRAFT = {};

export default function AddRoleFolderModal() {
    const { entitlements } = useOrganizationEntitlements();
    if (isFreeOrg(entitlements)) return <PlanRestrictedScreen />;
    return <AddRoleFolderModalInner />;
}

function AddRoleFolderModalInner() {
    const { id, parentFolderID } = useLocalSearchParams()
    const storeKey = id || "new"

    const queryClient = useQueryClient()
    const router = useRouter()

    const {data: folderToEdit} = Query.roleFolders.byId({id: id, options: {enabled: !!id, placeholderData: {}}})

    const setEditScreenData = useEditScreenStore(s => s.setEditScreenData)
    const removeEditScreenData = useEditScreenStore(s => s.removeEditScreenData)
    const folderDetails = useEditScreenStore(s => s.AddRoleFolderModal?.[storeKey]) || EMPTY_DRAFT

    useEffect(() => {   
        if(folderToEdit){
            setFolderDetails(folderToEdit);
        }
    }, [folderToEdit]);

    const setFolderDetails = (details) => {
        setEditScreenData("AddRoleFolderModal", storeKey, details)
    }


    const saveFolderMutation = useMutation({
        mutationFn: saveRoleFolder,
        onSuccess: () => {
            removeEditScreenData("AddRoleFolderModal", storeKey)
            queryClient.invalidateQueries({queryKey: QueryKeys.roleFolders.all()});
            router.back();
        }
    })
    
    return (
        <ModalWrapper 
            loading={saveFolderMutation.isPending}
            title={id ? "Edit Role Folder" : "New Role Folder"}
            onCancel={() => removeEditScreenData("AddRoleFolderModal", storeKey)}
            onConfirm={() => {
                const regex = new RegExp(/^[a-z 0-9_-]+$/i);
                if(!regex.test(folderDetails.name)){
                    Toast.show({type: 'error', text2: "Folder name can only contain letters, numbers, spaces, hyphens, and underscores!"})
                    return;
                }

                saveFolderMutation.mutate({...folderDetails, parentFolderID: parentFolderID})
            }}
        >
            <KeyboardAwareScrollView bottomOffset={100} keyboardShouldPersistTaps="handled">
                <InputSection>
                    <Input
                        title="Folder Name"
                        maxLength={120}
                        value={folderDetails.name}
                        onUpdate={(new_value) => {
                            setFolderDetails({name: new_value})
                        }}
                    />

                    <SheetSelector multiple type="roles" title="Roles" fromScreen={`roles/folders/create`} fromStoreKey={storeKey} value={folderDetails.Roles || []} onChange={(selected_items) => {
                            setFolderDetails({Roles: selected_items})
                        }} 
                    />

                    <Text style={styles.anyChosenRolesText}>Any chosen roles will be moved out of their existing folder and into this one</Text>
                </InputSection>
            </KeyboardAwareScrollView>
        </ModalWrapper>
    );
}

const styles = StyleSheet.create(({colors}) => ({
    anyChosenRolesText: {
        color:colors.textSecondary, 
        fontSize:12, 
        fontStyle:"italic", 
        paddingLeft:10
    }
}))
